import { useState, useMemo } from "react";
import { foldSearch } from "../../utils/searchFold";

// `foldSearch` returns "" for a missing field, so no separate guard is needed.
const matchesTerm = (user, term) =>
  foldSearch(user.name).includes(term) || foldSearch(user.email).includes(term);

export const useUserFiltering = (users, deactivatedUsers = []) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("User list");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Filter users based on search term (dashmote.com filtering handled server-side)
  const filteredUsers = useMemo(() => {
    const term = foldSearch(searchTerm);
    return users.filter((user) => matchesTerm(user, term));
  }, [users, searchTerm]);

  // How many DEACTIVATED users match the current term — drives the empty-search
  // hint that points the admin at the Deactivated tab (a deactivated user is
  // absent from the active list, so a search there reads as "missing"). Counted
  // by unique user. Zero when the box is empty. The guard reads the FOLDED term,
  // since folding trims: a whitespace-only box folds to "" and every row would
  // otherwise match it, lighting the hint up on an apparently empty search.
  const deactivatedMatchCount = useMemo(() => {
    const term = foldSearch(searchTerm);
    if (!term) return 0;
    const ids = new Set();
    for (const user of deactivatedUsers) {
      if (matchesTerm(user, term)) ids.add(user.userId);
    }
    return ids.size;
  }, [deactivatedUsers, searchTerm]);

  // Calculate unique user count from flattened data
  const uniqueUserCount = useMemo(() => {
    const uniqueUserIds = new Set(filteredUsers.map((user) => user.userId));
    return uniqueUserIds.size;
  }, [filteredUsers]);

  // Pagination — BY USER, not by flattened row. Each user is expanded into one
  // row per module and the Name/Role cells are merged with rowSpan; slicing the
  // flat list mid-user splits a user across pages, which breaks that grouping
  // (orphaned rows with shifted columns and a stray "+ Module"). Paginate the
  // ordered unique userIds instead, then keep every row of the page's users so a
  // user's module-rows always travel together. pageSize counts users.
  const orderedUserIds = useMemo(() => {
    const ids = [];
    const seen = new Set();
    for (const user of filteredUsers) {
      if (!seen.has(user.userId)) {
        seen.add(user.userId);
        ids.push(user.userId);
      }
    }
    return ids;
  }, [filteredUsers]);

  const startIndex = (currentPage - 1) * pageSize;
  const paginatedUsers = useMemo(() => {
    const pageUserIds = new Set(
      orderedUserIds.slice(startIndex, startIndex + pageSize)
    );
    return filteredUsers.filter((user) => pageUserIds.has(user.userId));
  }, [filteredUsers, orderedUserIds, startIndex, pageSize]);

  const handlePaginationChange = (page, size) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const handlePageSizeChange = (current, size) => {
    setCurrentPage(1); // Reset to first page when page size changes
    setPageSize(size);
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
  };

  return {
    searchTerm,
    setSearchTerm: handleSearchChange,
    viewMode,
    setViewMode: handleViewModeChange,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    filteredUsers,
    paginatedUsers,
    uniqueUserCount, // Add unique user count
    deactivatedMatchCount,
    handlePaginationChange,
    handlePageSizeChange,
  };
};
